<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useScrollLock } from '@/composables/useScrollLock';
import { useBackLayer } from '@/composables/useAppBack';
import { usePaymentsStore } from '@/store/modules/payments';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { cancelSubscription, resumeSubscription } from '@/api/PaymentApi';
import SmartwelSubscriptionInfoSheet from './SmartwelSubscriptionInfoSheet.vue';
import SmartwelUpsellSheet from './SmartwelUpsellSheet.vue';
import SmartwelPaymentSheet from './SmartwelPaymentSheet.vue';
import SmartwelCancelScreen from './SmartwelCancelScreen.vue';
import SmartwelSubscribeCompleteSheet from './SmartwelSubscribeCompleteSheet.vue';
import SmartwelGuideScreen from './SmartwelGuideScreen.vue';
import SmartwelToast from './SmartwelToast.vue';

const router = useRouter();
const paymentsStore = usePaymentsStore();
const primarySajuStore = usePrimarySajuStore();

/**
 * 복지찬스 구독 관련 시트 흐름을 한곳에 모은 공용 컨테이너.
 * - 구독중: openInfo() → 정보 시트 → 해지 화면 → 완료 토스트
 * - 비구독: openUpsell() → 권유 시트 → 결제 시트 → 시작 토스트
 * - 모바일/PC 표시 분기는 각 시트의 SmartwelResponsiveSheet가 담당
 * - 부모는 ref로 openInfo() / openUpsell()만 호출
 */
const showInfoSheet = ref(false);
const showUpsellSheet = ref(false);
const showPaymentSheet = ref(false);
const showCancelScreen = ref(false);
const showComplete = ref(false);
const hasSaju = ref(false);
const showGuide = ref(false);
const guideReturn = ref(null); // 'complete' | 'info' — 가이드 뒤로 시 복귀 대상
const toastMessage = ref('');

// 오버레이가 하나라도 열려 있으면 배경 스크롤 잠금 (중첩 시에도 단일 락)
const isOverlayOpen = computed(
  () =>
    showInfoSheet.value ||
    showUpsellSheet.value ||
    showPaymentSheet.value ||
    showCancelScreen.value ||
    showComplete.value ||
    showGuide.value,
);
useScrollLock(isOverlayOpen);

const showToast = (message) => {
  toastMessage.value = message;
};

const openInfo = () => {
  showInfoSheet.value = true;
};
const openUpsell = () => {
  showUpsellSheet.value = true;
};

const onSubscribe = () => {
  showUpsellSheet.value = false;
  showPaymentSheet.value = true;
};

const onPay = async () => {
  showPaymentSheet.value = false;
  // 내사주 유무로 완료 팝업 변형 분기 (없음 → 사주 등록 유도 / 있음 → 바로 이용)
  await primarySajuStore.fetchMySaju();
  hasSaju.value = !!primarySajuStore.mySajuId;
  showComplete.value = true;
};

// 완료 팝업 액션 (메인 UI 진입 — 닫기는 현재 화면 유지)
const onCompleteClose = () => {
  showComplete.value = false;
};
const onCompleteRegisterSaju = () => {
  showComplete.value = false;
  router.push('/mypage/moreinfo');
};
// 내사주 있음 — "지금 바로 이용하기" → 내 사주 분석 화면으로
const onCompleteUse = () => {
  showComplete.value = false;
  router.push(`/analysis/${primarySajuStore.mySajuId}`);
};
const onCompleteGuide = () => {
  showComplete.value = false;
  guideReturn.value = 'complete';
  showGuide.value = true;
};
const onGuideClose = () => {
  // 뒤로 → 가이드를 연 화면(완료 팝업/구독정보 시트)으로 복귀
  showGuide.value = false;
  if (guideReturn.value === 'complete') showComplete.value = true;
  else if (guideReturn.value === 'info') showInfoSheet.value = true;
  guideReturn.value = null;
};
const onGuideRegisterSaju = () => {
  showGuide.value = false;
  router.push('/mypage/moreinfo');
};

const onGuide = () => {
  showInfoSheet.value = false;
  guideReturn.value = 'info';
  showGuide.value = true;
};

const onResume = async () => {
  const sub = paymentsStore.activeSubscription;
  if (!sub?.subscriptionId) {
    showToast('구독 정보를 찾을 수 없습니다');
    return;
  }
  try {
    await resumeSubscription(sub.subscriptionId);
    await paymentsStore.fetchActiveSubscriptions(true);
    showInfoSheet.value = false;
    showToast('해지 신청이 취소되었어요');
  } catch (e) {
    showToast(e?.response?.data?.detail || '처리 중 오류가 발생했습니다');
  }
};

const onCancel = () => {
  showInfoSheet.value = false;
  showCancelScreen.value = true;
};

useBackLayer({
  id: 'smartwel-info-sheet',
  isOpen: showInfoSheet,
  closeFromBack: () => {
    showInfoSheet.value = false;
  },
});
useBackLayer({
  id: 'smartwel-upsell-sheet',
  isOpen: showUpsellSheet,
  closeFromBack: () => {
    showUpsellSheet.value = false;
  },
});
useBackLayer({
  id: 'smartwel-payment-sheet',
  isOpen: showPaymentSheet,
  closeFromBack: () => {
    showPaymentSheet.value = false;
  },
});
useBackLayer({
  id: 'smartwel-complete-sheet',
  isOpen: showComplete,
  closeFromBack: onCompleteClose,
});
useBackLayer({
  id: 'smartwel-guide-screen',
  isOpen: showGuide,
  closeFromBack: onGuideClose,
});
useBackLayer({
  id: 'smartwel-cancel-screen',
  isOpen: showCancelScreen,
  closeFromBack: () => {
    showCancelScreen.value = false;
  },
});

const onCancelConfirmed = async (cancelReason = '') => {
  showCancelScreen.value = false;
  const sub = paymentsStore.activeSubscriptions[0];
  if (!sub?.subscriptionId) {
    showToast('구독 정보를 찾을 수 없습니다');
    return;
  }
  try {
    await cancelSubscription(sub.subscriptionId, cancelReason);
    await paymentsStore.fetchActiveSubscriptions(true); // 해지 후 상태 갱신
    showToast('해지 신청이 완료되었어요');
  } catch (e) {
    showToast(e?.response?.data?.detail || '해지 처리 중 오류가 발생했습니다');
  }
};

defineExpose({ openInfo, openUpsell });
</script>

<template>
  <SmartwelSubscriptionInfoSheet
    v-if="showInfoSheet"
    @close="showInfoSheet = false"
    @guide="onGuide"
    @cancel="onCancel"
    @resume="onResume"
  />

  <SmartwelUpsellSheet
    v-if="showUpsellSheet"
    @close="showUpsellSheet = false"
    @subscribe="onSubscribe"
  />

  <SmartwelPaymentSheet v-if="showPaymentSheet" @close="showPaymentSheet = false" @pay="onPay" />

  <SmartwelSubscribeCompleteSheet
    v-if="showComplete"
    :has-saju="hasSaju"
    @close="onCompleteClose"
    @use="onCompleteUse"
    @register-saju="onCompleteRegisterSaju"
    @guide="onCompleteGuide"
  />

  <SmartwelGuideScreen
    v-if="showGuide"
    @close="onGuideClose"
    @register-saju="onGuideRegisterSaju"
  />

  <SmartwelCancelScreen
    v-if="showCancelScreen"
    @close="showCancelScreen = false"
    @confirm="onCancelConfirmed"
  />

  <SmartwelToast
    v-if="toastMessage"
    :key="toastMessage"
    :message="toastMessage"
    @close="toastMessage = ''"
  />
</template>
